import React from 'react';
import { Timer, Star, TrendingUp, BarChart3, Target, Award, Zap } from 'lucide-react';
import TrialConnxLogo from '../assets/tco-logo-text.svg';

const LandingPage = ({ onGetStarted }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex flex-col items-center justify-center mb-8">
            <img src={TrialConnxLogo} alt="TrialConnx" className="h-8 mb-6" />
            
            {/* Compelling Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight px-4">
              Discover Your Site's
              <span className="text-primary-600 block">Hidden Potential</span>
            </h1>
            
            {/* Time & Stats */}
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8 text-gray-600 px-4">
              <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-sm">
                <Timer className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
                <span className="font-medium text-sm sm:text-base">Takes 3-5 minutes</span>
              </div>
              <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-sm">
                <Star className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
                <span className="font-medium text-sm sm:text-base">Used by 1,200+ sites</span>
              </div>
              <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-sm">
                <TrendingUp className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" />
                <span className="font-medium text-sm sm:text-base">60% faster response</span>
              </div>
            </div>

            {/* Tool Name */}
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 ml-4">StudySpeed Calculator</h2>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8 mx-4 sm:mx-auto max-w-5xl hover-lift fade-in">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 text-center px-4">Get Your Free Assessment & Discover:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center group">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-200 transition-colors duration-200">
                  <BarChart3 className="w-6 h-6 text-primary-600 group-hover:scale-110 transition-transform duration-200" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Industry Benchmarks</h4>
                <p className="text-sm text-gray-600">Compare against 500+ research sites</p>
              </div>
              <div className="text-center group">
                <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-success-200 transition-colors duration-200">
                  <TrendingUp className="w-6 h-6 text-success-600 group-hover:scale-110 transition-transform duration-200" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">ROI Projections</h4>
                <p className="text-sm text-gray-600">Get personalized improvement estimates</p>
              </div>
              <div className="text-center group">
                <div className="w-12 h-12 bg-info-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-info-200 transition-colors duration-200">
                  <Target className="w-6 h-6 text-info-600 group-hover:scale-110 transition-transform duration-200" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Action Plan</h4>
                <p className="text-sm text-gray-600">Receive actionable improvement steps</p>
              </div>
              <div className="text-center group">
                <div className="w-12 h-12 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-warning-200 transition-colors duration-200">
                  <Award className="w-6 h-6 text-warning-600 group-hover:scale-110 transition-transform duration-200" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Performance Score</h4>
                <p className="text-sm text-gray-600">See how you rank vs industry leaders</p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Benchmark your clinical trial feasibility response times against industry standards and discover your improvement potential
          </p>

          {/* Get Started Button */}
          <button
            onClick={onGetStarted}
            className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 touch-manipulation"
          >
            Get Started - It's Free
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-center text-gray-500 text-sm">
            <span>Powered by</span>
            <img src={TrialConnxLogo} alt="TrialConnx" className="h-4 ml-2" />
          </div>
          <p className="text-gray-400 text-xs mt-2">
            Accelerating clinical research through smart feasibility solutions
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;